import { useQuery } from "@apollo/client";
import {
  midgardClients,
  DATA_DUMP_QUERY,
  DATA_DUMP_AGGREGATE_QUERY,
} from "providers/MidgardProvider";
import { ONE_SECOND_OF_MS } from "reports/utils/timeUtils";

import { ReportParams, TimeRangeFinite } from "../types";
import { useIsAdmin } from "utils/customHooks/genericHooks";
import { errorOverlayStateAtom } from "reports/components/ErrorOverlay";
import { useAtom } from "jotai";
import { useParams } from "react-router";

type DataDumpGetterProps = {
  range: TimeRangeFinite;
  tenant?: string;
  machemetryIds?: string[];
  shouldSkipQuery?: boolean;
  setData: any;
  isAggregate?: boolean; // can use this getter for dataDump or dataDumpAggregate
  multiData?: boolean; // sometimes an array of data dump getters are used
  shouldAllocate?: boolean;
};

export default function DataDumpGetter({
  range,
  tenant,
  machemetryIds = [],
  shouldSkipQuery,
  setData,
  isAggregate = false,
  shouldAllocate = false,
  multiData = false,
}: DataDumpGetterProps) {
  const ready = range.ge && range.lt; // don't query when lt = NaN

  return ready ? (
    <Query
      {...{
        range,
        tenant,
        machemetryIds,
        shouldSkipQuery,
        setData,
        isAggregate,
        multiData,
        shouldAllocate,
      }}
    />
  ) : null;
}

// tslint:disable-next-line: cognitive-complexity
function Query({
  range,
  tenant,
  machemetryIds,
  shouldSkipQuery,
  setData,
  isAggregate,
  multiData,
  shouldAllocate,
}: DataDumpGetterProps) {
  const { ckey } = useParams() as ReportParams;
  const isAdminUser = useIsAdmin();
  const [, setErrorOverlayState] = useAtom(errorOverlayStateAtom);

  const { loading, error, refetch } = useQuery(
    isAggregate ? DATA_DUMP_AGGREGATE_QUERY : DATA_DUMP_QUERY,
    {
      skip: shouldSkipQuery,
      client: midgardClients.get(tenant ?? ckey),
      variables: {
        ge: String(range.ge),
        lt: String(range.lt),
        cKey: tenant ?? ckey,
        machemetryIds,
        shouldAllocate,
      },
      notifyOnNetworkStatusChange: true,
      onError: err => {
        // refetch after a 10 second delay so we don't run through all of our servers memmory
        setTimeout(refetch, 10 * ONE_SECOND_OF_MS);
        console.log(
          `Data dump ${
            isAggregate ? " aggregate" : ""
          } query gone wrong. cKey: ${tenant ?? ckey}, ge: ${range?.ge}, lt: ${
            range.lt
          }, machemetryIds: ${machemetryIds}`
        );
        console.log(`retrying in 10 seconds`);
        if (isAdminUser || process.env.NODE_ENV === "development") {
          setErrorOverlayState({
            error: true,
            message: JSON.stringify(err, null, 2),
          });
        }
      },
      onCompleted: async data => {
        if (!shouldSkipQuery && data && !loading && !error) {
          const queryData =
            isAggregate && data?.dataDumpAggregate
              ? data.dataDumpAggregate
              : data?.dataDump
                ? data.dataDump
                : null;

          if (multiData) {
            await new Promise(r => setTimeout(r, Math.random() * 5000));
            setData(currentData => {
              return isAggregate // we can sort based on the GE's if its an aggregate
                ? [...currentData, queryData].sort(
                    ({ ge: ge1 }, { ge: ge2 }) => ge1 - ge2
                  )
                : [...currentData, { ge: range.ge, lt: range.lt, queryData }]; // regular data dumps require the range given for sorting
            });
          } else {
            setData(queryData);
          }
        }
      },
    }
  );

  return null;
}
